import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, Megaphone, ClipboardList } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/services/api";

interface Announce {
    id: string;
    header?: string;
    description?: string;
    image_url?: string;
    target_url?: string;
}

export default function AgencyHomeTab() {
    const navigate = useNavigate();
    const [announces, setAnnounces] = useState<Announce[]>([]);
    const [announcesLoading, setAnnouncesLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        api.get("/integrations/hub/agency-announces/")
            .then((res) => {
                if (res.data.status === "success") {
                    setAnnounces(
                        res.data.announces.map((a: Record<string, string>) => ({
                            id: a.id,
                            header: a.header,
                            description: a.description,
                            image_url: a.image_url,
                            target_url: a.target_url,
                        }))
                    );
                }
            })
            .catch(() => {})
            .finally(() => setAnnouncesLoading(false));
    }, []);

    const current = announces[currentIndex];
    const prevSlide = () => setCurrentIndex((i) => (i - 1 + announces.length) % announces.length);
    const nextSlide = () => setCurrentIndex((i) => (i + 1) % announces.length);

    const AnnouncementCarousel = ({ height }: { height: string }) => (
        <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full overflow-hidden rounded-3xl shadow-sm bg-white border border-slate-100"
        >
            {announcesLoading ? (
                <div className={`w-full ${height} flex items-center justify-center bg-slate-50`}>
                    <Loader2 className="h-8 w-8 text-slate-300 animate-spin" />
                </div>
            ) : announces.length === 0 ? (
                <div className={`w-full ${height} flex flex-col items-center justify-center bg-slate-50 gap-3`}>
                    <Megaphone className="h-10 w-10 text-slate-300" />
                    <p className="text-sm text-slate-400">There are no announcements to show</p>
                </div>
            ) : (
                <>
                    <div
                        className={`relative w-full ${height} bg-slate-50${current.target_url ? " cursor-pointer" : ""}`}
                        onClick={() => current.target_url && window.open(current.target_url, "_blank", "noopener,noreferrer")}
                    >
                        {current.image_url && (
                            <img
                                src={current.image_url}
                                alt={current.header || ""}
                                className="w-full h-full object-contain object-center select-none"
                                draggable={false}
                            />
                        )}
                        {announces.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                                    className="absolute top-1/2 left-0 -translate-y-1/2 bg-black/45 hover:bg-black/80 text-white w-11 h-16 flex items-center justify-center rounded-r text-xl z-10 transition-colors"
                                    aria-label="Previous"
                                >
                                    &#10094;
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                                    className="absolute top-1/2 right-0 -translate-y-1/2 bg-black/45 hover:bg-black/80 text-white w-11 h-16 flex items-center justify-center rounded-l text-xl z-10 transition-colors"
                                    aria-label="Next"
                                >
                                    &#10095;
                                </button>
                                <span className="absolute bottom-2.5 left-1/2 -translate-x-1/2 text-white text-xs bg-black/40 px-3 py-0.5 rounded-full z-10 whitespace-nowrap pointer-events-none">
                                    {currentIndex + 1} / {announces.length}
                                </span>
                            </>
                        )}
                    </div>
                    {(current.header || current.description) && (
                        <div className="px-4 py-3 text-left">
                            {current.header && (
                                <h3 className="text-base font-semibold text-slate-800 mb-1">{current.header}</h3>
                            )}
                            {current.description && (
                                <p className="text-sm text-slate-500">{current.description}</p>
                            )}
                        </div>
                    )}
                </>
            )}
        </motion.div>
    );

    return (
        <>
            <div className="md:hidden flex flex-col gap-3">
                <AnnouncementCarousel height="h-[260px]" />
                <button
                    onClick={() => navigate("/partner/applicant/create")}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-[#001B44] text-white text-sm font-semibold hover:bg-[#002a6e] transition-colors"
                >
                    <ClipboardList className="w-4 h-4" />
                    Apply Now
                </button>
            </div>

            <div className="hidden md:flex gap-4 items-start">
                <div className="flex-[3]">
                    <AnnouncementCarousel height="h-[420px]" />
                </div>
                <div className="flex-[1] flex flex-col">
                    <button
                        onClick={() => navigate("/partner/applicant/create")}
                        className="w-full h-[130px] flex flex-col items-center justify-center gap-3 rounded-3xl bg-[#001B44] text-white hover:bg-[#002a6e] transition-colors px-4"
                    >
                        <ClipboardList className="w-8 h-8" />
                        <span className="text-sm font-semibold text-center leading-snug">Apply Now</span>
                    </button>
                </div>
            </div>
        </>
    );
}
